import type { TFunction } from "i18next";
import type { ItemChange, ShapeViolation } from "../../../api/content/reviewDecoders";
import { iriLocalName } from "../../../shared/references/shortReferences";
import { plainTriple, type PlainTerm } from "../diff/plainTriples";

const ITEM_VERSION = /\/items\/([0-9a-f-]{36})\/versions\/([^/]+)$/;

/** The code a shape's message starts with ("AL-5: ", "QP-7: "): it names the shape in its file, never on the screen. */
const SHAPE_CODE = /^[A-Z]{2,}-\d+[a-z]?:\s+/u;
const IRI_IN_TEXT = /<?(https?:\/\/[^\s<>"'(),]+)>?/gu;

/**
 * A finding's message as people read it (usability round 6, P1): without its shape's code, and each full IRI the
 * message interpolates by its local name, as the data model names it ("BirthImpedimentState"). The raw message stays
 * available where the finding shows.
 */
export function findingText(message: string): string {
  return message.replace(SHAPE_CODE, "").replace(IRI_IN_TEXT, (_match, iri: string) => {
    const trimmed = iri.replace(/[.;:]+$/u, "");
    return iriLocalName(trimmed) + iri.slice(trimmed.length);
  });
}

/** The item version a finding is about, as a version reference (`<item>@<version>`), if its focus is one. */
export function itemVersionReference(focus: string | null): string | null {
  const match = focus === null ? null : ITEM_VERSION.exec(focus.replace(/^<(.*)>$/, "$1"));
  return match === null ? null : `${match[1] ?? ""}@${match[2] ?? ""}`;
}

/** The focus IRIs of item versions the review's own items do not name, with their references: names the item name cache can read. */
export function unnamedItemVersions(items: readonly ItemChange[], violations: readonly ShapeViolation[]): readonly { readonly iri: string; readonly reference: string }[] {
  const known = new Set(items.map((item) => item.itemId));
  return violations.flatMap((violation) => {
    const reference = itemVersionReference(violation.finding.focus);
    const iri = violation.finding.focus?.replace(/^<(.*)>$/, "$1");
    return reference === null || iri === undefined || known.has(reference.split("@")[0] ?? "") ? [] : [{ iri, reference }];
  });
}

/**
 * `labels` with the names of the review's items for the item versions shapes found (R-3):
 * a process shapes violation's focus is an item version, which no term label names.
 */
export function withItemNames(labels: Readonly<Record<string, string>>, items: readonly ItemChange[], violations: readonly ShapeViolation[]): Readonly<Record<string, string>> {
  const names = new Map(items.map((item) => [item.itemId, item.name]));
  const named = violations.flatMap((violation) => {
    const iri = violation.finding.focus?.replace(/^<(.*)>$/, "$1");
    const name = iri === undefined ? undefined : names.get(ITEM_VERSION.exec(iri)?.[1] ?? "");
    return iri !== undefined && name !== undefined ? [[iri, name] as const] : [];
  });
  return named.length === 0 ? labels : { ...labels, ...Object.fromEntries(named) };
}

/** A check's focus is an N3 term. Reuse the review's RDF parser and term names. */
export function findingFocus(focus: string | null, labels: Readonly<Record<string, string>>): PlainTerm | null {
  if (focus === null) return null;
  const term = plainTriple(`${focus} <urn:dmw:focus> <urn:dmw:focus> .`, labels)?.subject
    ?? { kind: "text", text: focus, language: null };
  // An item version's local name is a hash: its item's name says which it is.
  return term.kind === "term" && term.label !== null && ITEM_VERSION.test(term.iri) ? { ...term, name: term.label } : term;
}

/** The same focus in a plain-text comment, without HTML or technical IRIs. */
export function findingFocusName(focus: PlainTerm | null, t: TFunction): string {
  if (focus === null) return t("reviews.mergeBlocks.noFocus");
  switch (focus.kind) {
    case "term": return focus.label ?? focus.name;
    case "axiom": return t("reviews.item.axiom");
    case "text": return focus.text;
  }
}
